import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import './WordGroupPage.css';
import AddMoreWords from './AddMoreWords';


import OnScreenKeyboard from './OnScreenKeyboard';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';

import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';
import { isTextSafe } from '../utils/safeForAds';
import { getUserTimezone } from '../utils/timezoneUtils';
import { updateCountsAfterWordChange } from '../utils/countUpdates';
import { useData } from '../contexts/DataContext';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupData } from '../hooks/useWordGroupData';
import { useWordsData } from '../hooks/useWordsData';
import { useIsClassroomHead } from '../hooks/useIsClassroomHead';

const WordGroupPage = () => {
  const { id, groupId } = useParams();
  const navigate = useNavigate();

  const { t, isRTL } = useLanguage();
  const { refetchUsage } = useOutletContext() || {};

  // Use DataContext hooks
  const { refreshModules } = useData();
  const { moduleData, loading: moduleLoading, refetch: refetchModuleData } = useModuleData(id);
  const { groupData, loading: groupLoading, refetch: refetchGroupData } = useWordGroupData(id, groupId);
  const { words, loading: wordsLoading, refetch: refetchWords } = useWordsData(groupData?.id);
  const isHead = useIsClassroomHead(moduleData?.classroom_id);
  
  // Determine if user can edit/delete
  // For private modules (no classroom_id), user can always edit
  // For classroom modules, only heads can edit
  const canEdit = moduleData?.classroom_id ? (isHead === true) : true;

  const [error, setError] = useState('');
  const [showAddWords, setShowAddWords] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedWords, setEditedWords] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [practiceError, setPracticeError] = useState('');
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const [focusedLang, setFocusedLang] = useState(null);
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
  const wordToInputRefs = useRef([]);
  const keyboardOverlayRef = useRef(null);



  // Close keyboard when clicking outside input or keyboard overlay, but only if not locked
  useEffect(() => {
    if (!focusedLang) return;
    function handleClick(e) {
      const input = wordToInputRefs.current[focusedInputIdx];
      const overlay = keyboardOverlayRef.current;
      if (
        input && !input.contains(e.target) &&
        overlay && !overlay.contains(e.target)
      ) {
        setFocusedLang(null);
        setFocusedInputIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [focusedLang, focusedInputIdx]);



  const handleWordsAdded = async (newWords) => {
    if (!groupData || !groupData.id) return;
    // Normalize to { left, right }
    const fromKey = moduleData.from_lang;
    const toKey = moduleData.to_lang;
    const normalizedPairs = (newWords || []).map(pair => ({
      left: pair.left ?? pair[fromKey],
      right: pair.right ?? pair[toKey]
    }));
    const validPairs = normalizedPairs.filter(
      pair => pair && typeof pair.left === 'string' && pair.left.trim() && typeof pair.right === 'string' && pair.right.trim()
    );
    if (validPairs.length === 0) return;

    // Get current max position in this group to avoid overlap
    const { data: existingWords, error: wordsCountError } = await supabase
      .from('words')
      .select('position')
      .eq('group_id', groupData.id)
      .order('position', { ascending: false })
      .limit(1);

    if (wordsCountError) {
      console.error('Error fetching words count:', wordsCountError);
      return;
    }

    const maxPosition = (!existingWords || existingWords.length === 0)
      ? -1
      : existingWords[0].position;

    const wordsToInsert = validPairs.map((pair, idx) => ({
      group_id: groupData.id,
      from_text: pair.left.trim(),
      to_text: pair.right.trim(),
      position: maxPosition + 1 + idx,
      // SRS fields removed - now stored in word_srs_data table
    }));

    // Insert to DB
    await supabase.from('words').insert(wordsToInsert);

    // Update counts after word insertion
    await updateCountsAfterWordChange(groupData.id);

    // Refresh module and word group data to update counts
    refetchModuleData();
    refetchGroupData();

    // Refresh dashboard modules to update counts
    refreshModules();

    // If this is a classroom module, refresh classrooms list
    if (moduleData?.classroom_id) {
      window.dispatchEvent(new Event('refreshClassrooms'));
    }

    // Refresh words list to show newly added words
    refetchWords();

    setShowAddWords(false);
    if (refetchUsage) refetchUsage();
  };

  if (moduleLoading || groupLoading || wordsLoading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        {t('loading') || 'Loading...'}
      </div>
    );
  }

  if (!moduleData || !groupData) return (
    <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
      <h2>{t('wordGroupNotFound')}</h2>
      <p>{t('wordGroupNotFoundMessage')}</p>

      <Link to="/" className="btn btn-primary">{t('backToModules')}</Link>
    </div>
  );

  // Show empty state if no words (not loading)
  if (!words || words.length === 0) {
    return (
      <div className="word-group-page-container">
        {/* Top Banner */}
        <div className="word-group-banner">
          <div className="banner-content">
            <button onClick={() => navigate(`/module/${moduleData.id}`)} className="btn btn-secondary btn-back">
              {isRTL ? '→' : '←'} {t('backToModule').replace(/^([←→]\s*)?/, '')}
            </button>
            <h2 className="banner-title">{groupData.name}</h2>
            <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.2rem', textAlign: 'center' }}>
              0 {t('flashcard', 0)}
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ margin: '1rem 0', textAlign: 'center', color: 'red' }}>{error}</div>
        )}

        {/* Empty State */}
        <div className="card" style={{ padding: '2rem', textAlign: 'center', margin: '2rem auto', maxWidth: '600px' }}>
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#666', marginBottom: '1rem' }}>
              {t('noWordsYet') || 'No words yet'}
            </h3>
            <p style={{ color: '#888', marginBottom: '2rem' }}>
              {t('addYourFirstWords') || 'Add your first words to get started!'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddWords(true)}
              style={{ minWidth: '120px' }}
            >
              {t('addWords') || 'Add Words'}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/module/${moduleData.id}`)}
              style={{ minWidth: '120px' }}
            >
              {t('backToModule') || 'Back to Module'}
            </button>
          </div>
        </div>

        {/* Add More Words panel */}
        {showAddWords && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '2.5rem' }}>
            <AddMoreWords
              moduleId={id}
              groupId={groupData?.id}
              groupData={groupData}
              onWordsAdded={handleWordsAdded}
              refetchUsage={refetchUsage}
            />
          </div>
        )}
      </div>
    );
  }

  const handleStartFlashcards = () => {

    navigate(`/module/${moduleData.id}/group/${groupData.id}/flashcard-mode`);
  };

  const handleEditWords = () => {
    setIsEditing(true);
    // Always edit in oldest→newest position order
    setEditedWords([...(words || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
  };

  const handleSaveWords = async () => {
    setIsSaving(true);
    setError(''); // Clear previous errors
    try {
      // Check all edited words for inappropriate content
      for (let i = 0; i < editedWords.length; i++) {
        const word = editedWords[i];
        if (!isTextSafe(word.from_text)) {
          setError(t('inappropriateContentDetected'));
          setIsSaving(false);
          return;
        }
        if (!isTextSafe(word.to_text)) {
          setError(t('inappropriateContentDetected'));
          setIsSaving(false);
          return;
        }
      }
      // Find deleted words (present in words but not in editedWords)
      const deletedWords = (words || []).filter(w => !editedWords.some(ew => ew.id === w.id));
      // Only update changed words and reset SRS if the word text changes
      const changedWords = editedWords.filter((word, i) => {
        const original = words[i];
        return (
          !original ||
          word.from_text !== original.from_text ||
          word.to_text !== original.to_text ||
          i !== original.position
        );
      });
      // Delete removed words
      await Promise.all(
        deletedWords.map(word =>
          supabase.from('words').delete().eq('id', word.id)
        )
      );
      // Update changed words (only content, SRS is in word_srs_data)
      await Promise.all(
        changedWords.map((word, i) => {
          const original = (words || []).find(w => w.id === word.id);
          const textChanged = !original || word.from_text !== original.from_text || word.to_text !== original.to_text;
          
          // Update word content
          const updatePromise = supabase.from('words').update({
            from_text: word.from_text,
            to_text: word.to_text,
            position: editedWords.indexOf(word),
            group_id: groupData.id,
          }).eq('id', word.id);
          
          // If word text changed, reset SRS data for all users in word_srs_data
          if (textChanged) {
            // Delete all SRS data for this word (users will start fresh)
            return Promise.all([
              updatePromise,
              supabase.from('word_srs_data').delete().eq('word_id', word.id)
            ]);
          }
          
          return updatePromise;
        })
      );

      // Reorder positions of remaining words to fill gaps (0, 1, 2, etc.)
      // Keep oldest→newest visual order (editedWords is already sorted that way)
      const remainingWords = editedWords.filter(word => !deletedWords.some(dw => dw.id === word.id));


      if (remainingWords.length > 0) {
        await Promise.all(
          remainingWords.map((word, newPosition) => {

            return supabase.from('words').update({
              position: newPosition
            }).eq('id', word.id);
          })
        );

      }
      // Update counts after word changes (deletions and updates)
      await updateCountsAfterWordChange(groupData.id);

      // Refresh module and word group data to update counts
      refetchModuleData();
      refetchGroupData();

      // Refresh dashboard modules to update counts
      refreshModules();

      // If this is a classroom module, refresh classrooms list
      if (moduleData?.classroom_id) {
        window.dispatchEvent(new Event('refreshClassrooms'));
      }

      // Refresh words list to show updated words
      refetchWords();

      setIsEditing(false);
      setShowAddWords(false);
    } catch (error) {
      console.error('Error saving words:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedWords(words || []);
    setShowAddWords(false);
  };

  const handleWordChange = (index, field, value) => {
    const updatedWords = [...editedWords];
    updatedWords[index] = {
      ...updatedWords[index],
      [field]: value
    };
    setEditedWords(updatedWords);
  };

  const handleDeleteWord = (index) => {
    const updatedWords = editedWords.filter((_, i) => i !== index);
    setEditedWords(updatedWords);
  };

  const checkPracticeLimitAndNavigate = async (practiceType) => {
    setPracticeError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const res = await fetch(`${backendUrl}/usage/check-practice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          timezone: getUserTimezone()
        })
      });
      const data = await res.json();
      if (data.canPractice) {
        if (practiceType === 'mcq') {
          navigate(`/module/${moduleData.id}/group/${groupData.id}/mcq-practice`);
        } else if (practiceType === 'fib') {
          navigate(`/module/${moduleData.id}/group/${groupData.id}/practice`);
        } else if (practiceType === 'story') {
          navigate(`/module/${moduleData.id}/group/${groupData.id}/story`);
        }
      } else {
        setPracticeError(data.error || t('dailyPracticeLimitReached'));
      }
    } catch (error) {
      setPracticeError(t('dailyPracticeLimitReached'));
    }
  };

  return (
    <div className="word-group-page-container">
      {/* Top Banner */}
      <div className="word-group-banner">
        <div className="banner-content">
          <button onClick={() => navigate(`/module/${moduleData.id}`)} className="btn btn-secondary btn-back">
            {isRTL ? '→' : '←'} {t('backToModule').replace(/^([←→]\s*)?/, '')}
          </button>
          <h2 className="banner-title">{groupData.name}</h2>
          <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.2rem', textAlign: 'center' }}>
            {words ? words.length : 0} {t('flashcard', words ? words.length : 0)}
          </div>
          <div className="banner-actions">
            <button onClick={handleStartFlashcards} className="btn btn-primary">
              {t('startFlashcards')}
            </button>
            {moduleData.from_lang !== moduleData.to_lang && (
              <>
                <button onClick={() => checkPracticeLimitAndNavigate('mcq')} className="btn btn-primary">
                  {t('practiceMultipleChoice')}
                </button>
                <button onClick={() => checkPracticeLimitAndNavigate('fib')} className="btn btn-primary">
                  {t('practiceSentences')}
                </button>
                <button onClick={() => checkPracticeLimitAndNavigate('story')} className="btn btn-primary">
                  {t('readAStory')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {practiceError && (
        <div className="error-message" style={{ margin: '1rem 0', textAlign: 'center' }}>
          <p>{practiceError}</p>
          {practiceError.includes('Want to learn more?') && (
            <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
              <Link
                to="/store"
                className="btn btn-primary"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                {t('upgradeToPremium')}
              </Link>
            </div>
          )}
        </div>
      )}
      {error && (
        <div className="error-message" style={{ margin: '1rem 0', textAlign: 'center', color: 'red' }}>{error}</div>
      )}

      {/* Edit Controls */}
      {canEdit && (
        <div className="edit-controls" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          {!isEditing && (
            <>
              <button onClick={handleEditWords} className="btn btn-secondary">
                {t('editWords')}
              </button>
              <button onClick={() => setShowAddWords(v => !v)} className="btn btn-secondary">
                {t('addMoreWords')}
              </button>
            </>
          )}
          {isEditing && (
            <div className="edit-actions">
              <button onClick={handleSaveWords} className="btn btn-primary" disabled={isSaving}>
                {isSaving ? t('saving') : t('saveChanges')}
              </button>
              <button onClick={handleCancelEdit} className="btn btn-secondary">
                {t('cancel')}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Add More Words panel below the buttons */}
      {showAddWords && !isEditing && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '2.5rem' }}>
          <AddMoreWords
            moduleId={id}
            groupId={groupData?.id}
            groupData={groupData}
            onWordsAdded={handleWordsAdded}
            refetchUsage={refetchUsage}
          />
        </div>
      )}

      <div className="card word-list-card">
        {/* Column Headers */}
        <div className="word-list-header">
          <span className="word-header-from">
            {moduleData.from_lang === moduleData.to_lang ? t('term') : t(moduleData.from_lang)}
          </span>
          <span
            className="word-header-to"
            style={isRTL ? { textAlign: 'left' } : {}}
          >
            {moduleData.from_lang === moduleData.to_lang ? t('definition') : t(moduleData.to_lang)}
          </span>
        </div>

        {(isEditing ? editedWords : words).map((word, idx) => {
          const targetLangCode = getKeyboardLangCode(moduleData.to_lang);
          const sourceLangCode = getKeyboardLangCode(moduleData.from_lang);
          const isTargetRTL = isRTLanguage(targetLangCode);
          const isSourceRTL = isRTLanguage(sourceLangCode);
          return (
            <div key={word.id || idx} className="word-list-row">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={word.from_text}
                    onChange={(e) => handleWordChange(idx, 'from_text', e.target.value)}
                    className="word-input word-from-input"
                    placeholder={moduleData.from_lang === moduleData.to_lang ? t('term') : `${t(moduleData.from_lang)} word`}
                    style={{
                      direction: isSourceRTL ? 'rtl' : 'ltr',
                      textAlign: isSourceRTL ? 'right' : 'left',
                    }}
                  />
                  <div className="word-edit-controls" style={{ width: '100%' }}>
                    <div className="input-with-keyboard-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <input
                        type="text"
                        value={word.to_text}
                        onChange={(e) => handleWordChange(idx, 'to_text', e.target.value)}
                        className="word-input word-to-input"
                        placeholder={moduleData.from_lang === moduleData.to_lang ? t('definition') : `${t(moduleData.to_lang)} word`}
                        style={{
                          direction: isTargetRTL ? 'rtl' : 'ltr',
                          textAlign: isTargetRTL ? 'right' : 'left',
                          paddingRight: !isTargetRTL ? '2.2rem' : undefined,
                          paddingLeft: isTargetRTL ? '2.2rem' : undefined,
                          marginLeft: 'auto',
                          width: '100%',
                          maxWidth: 320
                        }}
                        ref={el => wordToInputRefs.current[idx] = el}
                        onFocus={() => {
                          setFocusedInputIdx(idx);
                          setFocusedLang(getKeyboardLangCode(moduleData.to_lang));
                          if (keyboardArmedLang === getKeyboardLangCode(moduleData.to_lang)) {
                            setKeyboardActiveIdx(idx);
                          }
                        }}
                        onBlur={e => {
                          setFocusedInputIdx(null);
                          setFocusedLang(null);
                          setTimeout(() => {
                            const active = document.activeElement;
                            const isKeyboardKey = active && (
                              active.classList.contains('keyboard-key') ||
                              active.closest('.on-screen-keyboard-overlay')
                            );
                            const isSameLang = wordToInputRefs.current.some((input, i) =>
                              input === active && getKeyboardLangCode(moduleData.to_lang) === keyboardArmedLang
                            );
                            if (!isKeyboardKey && !isSameLang) {
                              setKeyboardActiveIdx(null);
                            }
                          }, 100);
                        }}
                      />
                      {focusedInputIdx === idx && (
                        <div className="keyboard-icon-absolute" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isTargetRTL ? 'auto' : '0.3rem', left: isTargetRTL ? '0.3rem' : 'auto', zIndex: 2 }}>
                          <button
                            type="button"
                            className="btn-toggle-keyboard subtle-toggle"
                            tabIndex={-1}
                            style={{
                              marginBottom: '0.25rem',
                              fontSize: '1.1rem',
                              background: 'none',
                              border: 'none',
                              color: '#888',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              lineHeight: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '1.8rem',
                              width: '1.8rem',
                            }}
                            aria-label={t('showKeyboard')}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const langCode = getKeyboardLangCode(moduleData.to_lang);
                              if (keyboardArmedLang === langCode) {
                                setKeyboardArmedLang(null);
                                setKeyboardActiveIdx(null);
                              } else {
                                setKeyboardArmedLang(langCode);
                                setKeyboardActiveIdx(idx);
                                wordToInputRefs.current[idx]?.focus();
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="5" width="18" height="14" rx="2" />
                              <line x1="7" y1="15" x2="7" y2="15" />
                              <line x1="11" y1="15" x2="11" y2="15" />
                              <line x1="15" y1="15" x2="15" y2="15" />
                              <line x1="7" y1="11" x2="7" y2="11" />
                              <line x1="11" y1="11" x2="11" y2="11" />
                              <line x1="15" y1="11" x2="15" y2="11" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => handleDeleteWord(idx)}
                        className="btn-delete-word"
                        title={t('deleteWord')}
                        aria-label={t('deleteWord')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <span className="word-from">{word.from_text}</span>
                  <span
                    className="word-to"
                    style={{
                      direction: isTargetRTL ? 'rtl' : 'ltr',
                      textAlign: isTargetRTL ? 'right' : 'left'
                    }}
                  >
                    {word.to_text}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div ref={keyboardOverlayRef}>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={(char) => {
              const input = wordToInputRefs.current[keyboardActiveIdx];
              if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const currentValue = input.value;

                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    input.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    input.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  input.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  input.setSelectionRange(start + char.length, start + char.length);
                }

                // Determine which field is being edited
                const isFromInput = input.classList.contains('word-from-input');
                const isToInput = input.classList.contains('word-to-input');
                if (isFromInput) {
                  handleWordChange(keyboardActiveIdx, 'from_text', newValue);
                } else if (isToInput) {
                  handleWordChange(keyboardActiveIdx, 'to_text', newValue);
                }

                // Keep focus on the input
                input.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default WordGroupPage;
